/**
 * NotificationService.js
 * 
 * Service to manage user notifications in Firestore.
 */

// Firebase
import { Timestamp, collection, deleteDoc, doc, onSnapshot, orderBy, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { NotificationPriority, NotificationSourceType, NotificationType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { getPersonName } from 'utilities/PersonIdentity';

// Helper to check for non-empty string
const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

const normalizeWorkspaceKey = (value) => {
    const key = String(value || '').trim();
    return key || null;
};

const resolveWorkspaceKey = ({ workspaceKey, metadata, actions } = {}) => {
    const topLevelWorkspaceKey = normalizeWorkspaceKey(workspaceKey);
    if (topLevelWorkspaceKey) return topLevelWorkspaceKey;

    const metadataWorkspaceKey = normalizeWorkspaceKey(metadata?.workspaceKey || metadata?.workspaceId);
    if (metadataWorkspaceKey) return metadataWorkspaceKey;

    if (Array.isArray(actions)) {
        for (const action of actions) {
            const actionWorkspaceKey = normalizeWorkspaceKey(
                action?.data?.workspaceKey || action?.data?.workspaceId
            );
            if (actionWorkspaceKey) return actionWorkspaceKey;
        }
    }

    return null;
};

/**
 * Marks a notification as read.
 * Also updates dateModified to now.
 * 
 * @param {string} notificationKey - The key of the notification to mark as read.
 */
export async function markNotificationAsRead(notificationKey) {
    // Validate input
    if (!isNonEmptyString(notificationKey)) return;

    // Update the notification document
    const ref = doc(db, collections.notifications, notificationKey);
    await updateDoc(ref, {
        isRead: true,
        dateModified: Timestamp.now(),
    });

}

/**
 * Marks multiple notifications as read.
 * Accepts an array of keys and/or notification objects.
 * Also updates dateModified to now.
 * 
 * @param {Array<string|object>} notificationKeys - Array of notification keys or notification objects.
 */
export async function markNotificationsAsRead(notificationKeys) {

    // Validate input
    if (!Array.isArray(notificationKeys) || notificationKeys.length === 0) return;

    // Extract keys from objects if necessary
    const keys = notificationKeys.filter(isNonEmptyString);

    // Return early if no valid keys
    if (keys.length === 0) return;

    // Remove duplicates
    const uniqueKeys = Array.from(new Set(keys));

    // Prepare batch update
    const now = Timestamp.now();

    // Firestore batch write
    const batch = writeBatch(db);

    // Update each notification
    uniqueKeys.forEach((key) => {
        batch.update(doc(db, collections.notifications, key), {
            isRead: true,
            dateModified: now,
        });
    });

    // Commit the batch
    await batch.commit();
}

/**
 * Convenience helper: marks only unread notifications as read.
 * 
 * @param {Array<object>} notifications - Array of notification objects.
 */
export async function markUnreadNotificationsAsRead(notifications) {
    // Validate input
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    // Filter for unread notifications
    const unread = notifications.filter((n) => n && n.isRead !== true);

    // Return early
    if (unread.length === 0) return;

    // Mark them as read
    return markNotificationsAsRead(unread.map((n) => n?.key).filter(isNonEmptyString));
}

/**
 * Subscribes to a user's notifications ordered by newest first.
 * Returns the Firestore unsubscribe function.
 * 
 * @param {string} userKey - The user key to subscribe notifications for.
 * @param {function} onUpdate - Callback invoked with an array of notification objects on updates.
 * @param {function} [onError] - Optional error callback.
 * 
 * @returns {function} Unsubscribe function to stop listening.
 */
export function subscribeToUserNotifications(userKey, onUpdate, onError) {

    // Validate input
    if (!userKey) {
        onUpdate?.([]);
        return () => { };
    }

    // Build the query
    const notificationsDatabase = collection(db, collections.notifications);

    // Query for notifications for the user, ordered by dateCreated descending
    const q = query(
        notificationsDatabase,
        where('userKey', '==', userKey),
        orderBy('dateCreated', 'desc')
    );

    // Set up the real-time listener
    return onSnapshot(
        q,
        (snapshot) => {
            const notifications = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            onUpdate?.(notifications);
        },
        (error) => {
            if (onError) return onError(error);
            console.error('Error fetching notifications:', error);
        }
    );
}

/**
 * Creates a new notification document in the notifications collection.
 * Returns the created notification key.
 * 
 * @param {object} params - Notification parameters.
 * @param {string} params.userKey - The user key to whom the notification is addressed.
 * @param {string} params.type - The type of notification (use NotificationType enum).
 * @param {string} params.title - The title/text of the notification.
 * @param {string|null} [params.subtitle=null] - Optional subtitle text.
 * @param {string} [params.sourceType=NotificationSourceType.ENGAGEMENT] - Source type of the notification.
 * @param {string|null} [params.sourceKey=null] - Optional source key related to the notification.
 * @param {string|null} [params.workspaceKey=null] - Optional workspace key associated with this notification.
 * @param {object} [params.metadata={}] - Optional metadata object for additional info.
 * @param {string} [params.priority=NotificationPriority.NORMAL] - Priority level of the notification.
 * @param {Array<object>} [params.actions=[]] - Optional array of action objects for the notification.
 * 
 * @returns {Promise<string>} The key of the created notification document.
 */
export async function createNotification({
    userKey,
    type,
    title,
    subtitle = null,
    sourceType = NotificationSourceType.ENGAGEMENT,
    sourceKey = null,
    workspaceKey = null,
    metadata = {},
    priority = NotificationPriority.NORMAL,
    actions = [],
} = {}) {
    // Generate a new notification key
    const key = generateKey();

    const normalizedMetadata = metadata && typeof metadata === 'object' ? { ...metadata } : {};
    const normalizedActions = Array.isArray(actions) ? actions : [];

    const resolvedWorkspaceKey = resolveWorkspaceKey({
        workspaceKey,
        metadata: normalizedMetadata,
        actions: normalizedActions,
    });

    if (resolvedWorkspaceKey && !normalizeWorkspaceKey(normalizedMetadata.workspaceKey)) {
        normalizedMetadata.workspaceKey = resolvedWorkspaceKey;
    }

    // Prepare the notification object
    const now = Timestamp.now();
    const notification = {
        key,
        userKey,
        workspaceKey: resolvedWorkspaceKey,
        type,
        title,
        subtitle,
        sourceType,
        sourceKey,
        metadata: normalizedMetadata,
        priority,
        actions: normalizedActions,
        isRead: false,
        dateCreated: now,
        dateModified: now,
    };

    // Create the notification document
    await setDoc(doc(db, collections.notifications, key), notification);

    // Return the created notification key
    return key;
}

/**
 * Deletes a notification document.
 * 
 * @param {string} notificationKey - The key of the notification to delete.
 */
export async function deleteNotification(notificationKey) {

    // Validate input
    if (!isNonEmptyString(notificationKey)) return;

    // Delete the notification document
    await deleteDoc(doc(db, collections.notifications, notificationKey));
}

/**
 * Creates a notification from an engagement record.
 * Returns the created notification key, or null if it should be skipped.
 * 
 * @param {object} engagement - The engagement record.
 * @param {object} actor - The user who performed the engagement.
 * @param {object} targetUser - The user to notify.
 * @param {object|null} [itemSummary=null] - Optional summary of the item involved in the engagement.
 * 
 * @returns {Promise<string|null>} The key of the created notification, or null if skipped.
 */
export async function createNotificationFromEngagement(engagement, actor, targetUser, itemSummary = null) {

    // Validate input
    const { type, text, key: sourceKey } = engagement || {};

    // Skip notifications when the actor and target are the same user
    if (!actor?.key || !targetUser?.key || actor.key === targetUser.key) {
        return null;
    }

    let notificationType;
    let title;
    const actorDisplayName = getPersonName(actor, 'Someone');

    // Determine notification type and title based on engagement type
    switch (type) {
        case 'NODE_LIKE':
            notificationType = NotificationType.ENGAGEMENT_LIKE;
            title = `${actorDisplayName} liked your post`;
            break;
        case 'NODE_COMMENT':
            notificationType = NotificationType.ENGAGEMENT_COMMENT;
            title = `${actorDisplayName} commented: ${text}`;
            break;
        case 'NODE_FAVORITE':
            notificationType = NotificationType.ENGAGEMENT_FAVORITE;
            title = `${actorDisplayName} added your post to favorites`;
            break;
        case 'NODE_SHARE':
            notificationType = NotificationType.ENGAGEMENT_SHARE;
            title = `${actorDisplayName} shared your post`;
            break;
        case 'CONTRIBUTION_MADE':
            notificationType = NotificationType.ENGAGEMENT_CONTRIBUTION;
            title = `${actorDisplayName} contributed to your post`;
            break;
        case 'SESSION_SET':
            notificationType = NotificationType.ENGAGEMENT_SESSION;
            title = `${actorDisplayName} booked an appointment`;
            break;
        case 'USER_VIEW':
            notificationType = NotificationType.ENGAGEMENT_USER_VIEW;
            title = `${actorDisplayName} viewed your profile`;
            break;
        default:
            // Skip unknown engagement types
            return null;
    }

    // Prepare metadata
    const workspaceKey = normalizeWorkspaceKey(
        engagement?.workspaceKey
        || engagement?.workspaceId
        || engagement?.itemSummary?.workspaceKey
        || engagement?.itemSummary?.workspaceId
        || engagement?.itemSummary?.workspace?.key
        || itemSummary?.workspaceKey
        || itemSummary?.workspaceId
        || itemSummary?.workspace?.key
    );

    const metadata = {
        actorKey: actor.key,
        actorName: actorDisplayName,
        itemKey: itemSummary?.key,
        itemTitle: itemSummary?.title,
        engagementText: text,
        ...(workspaceKey ? { workspaceKey } : {}),
    };

    // Add navigation action for most types
    const actions = [];
    if (itemSummary?.key) {
        actions.push({
            label: 'View',
            action: 'NAVIGATE',
            data: { section: 'NODE', nodeKey: itemSummary.key },
        });
    }

    // Create the notification document
    return createNotification({
        userKey: targetUser.key,
        type: notificationType,
        title,
        sourceType: NotificationSourceType.ENGAGEMENT,
        sourceKey,
        workspaceKey,
        metadata,
        actions,
    });
}
